import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import { Box, Container } from "@mui/material";
import BackgroundImageCst from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import ProjectFormCst from "../ProjectFormCst";
import { useFetchDetail, usePostDetail } from "@hooks/useFetchDetail";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAddEditProject } from "../hooks/useAddEditProject";

const AddProject: React.FC = () => {
  const { t } = useTranslation(["projects-page"]);

  const { columns, actionsForForms } = useFetchDetail(
    ServicesURL.getProjects,
    undefined,
    PagesID["projects"],
  );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetProjectAssociations,
    setFinancingPlanAssociation,
    PMProjectAssociation,
    customerAssociation,
    scopeAssociation,
    subScopeAssociation,
    costCenterAssociation,
    openAssociations,
    financingPlanAssociation,
  } = useAddEditProject([]);

  const navigate = useNavigate();

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { trigger: triggerProject } = usePostDetail(ServicesURL.postProjects);

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const objToSend: any = {
      code: inputForm.code || null,
      title: inputForm.title || null,
      allocated_budget: Number(inputForm.allocated_budget) || null,
      project_manager_id:
        PMProjectAssociation && PMProjectAssociation.length > 0
          ? parseInt(PMProjectAssociation[0].id)
          : null,
      cost_center_id:
        costCenterAssociation && costCenterAssociation.length > 0
          ? parseInt(costCenterAssociation[0].id)
          : null,
      description: inputForm.description || null,
      expected_start_date: !!inputForm.expected_start_date
        ? dayjs(new Date(inputForm.expected_start_date)).format("YYYY-MM-DD")
        : null,
      expected_end_date: !!inputForm.expected_end_date
        ? dayjs(new Date(inputForm.expected_end_date)).format("YYYY-MM-DD")
        : null,
      customers_ids:
        customerAssociation && customerAssociation.length > 0
          ? customerAssociation.map(({ id }: any) => parseInt(id))
          : null,
      scope_id:
        scopeAssociation && scopeAssociation.length > 0
          ? parseInt(scopeAssociation[0].id)
          : null,
      sub_scope_id:
        subScopeAssociation && subScopeAssociation.length > 0
          ? parseInt(subScopeAssociation[0].id)
          : null,
      financing_plans_ids:
        financingPlanAssociation && financingPlanAssociation.length > 0
          ? financingPlanAssociation.map(({ id }: any) => parseInt(id))
          : null,
      funded: inputForm.funded || false,
    };

    try {
      await triggerProject(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
    }
  };

  const getColumns = useMemo(() => {
    return columns;
  }, []);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-project.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("add.hero-title")}
          description={t("add.hero-description")}
        />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <ProjectFormCst
              columns={getColumns}
              onSubmit={onSubmit}
              backButton={() => {
                navigate(-1);
              }}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              handleDelete={(accessorKey, value) =>
                handleDelete(accessorKey, value)
              }
              getValues={(accessorKey) => getValues(accessorKey)}
              association={openAssociations}
              fullWidth={true}
              resetProjectAssociations={resetProjectAssociations}
              scopeAssociation={scopeAssociation}
              costCenterAssociation={costCenterAssociation}
              setFinancingPlanAssociation={setFinancingPlanAssociation}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AddProject;
